/*
  Mobile sticky Add-to-Cart bar — DESIGN.md block M, owner requirement ④.
  Modelled on IKEA's mobile buy bar: always present, price beside the button,
  selected variant echoed underneath.

  The bar deliberately owns no cart logic. It reads the real Dawn buy area and
  forwards taps to it, so variant switching, sold-out state, pre-order copy and
  the cart drawer keep working exactly as they do without the bar.

  IMPORTANT: every element it mirrors is re-queried on each sync, never cached.
  Dawn replaces the price and button nodes wholesale when the variant changes,
  so a reference held from page load goes stale and silently stops updating —
  which is exactly how the bar came to show a varnished price beside a raw-wood
  variant.
*/
class JoykidsStickyAtc extends HTMLElement {
  connectedCallback() {
    this.priceTarget = this.querySelector('[data-jk-price]');
    this.variantTarget = this.querySelector('[data-jk-variant]');
    this.button = this.querySelector('[data-jk-add]');

    // The section wrapper is the one node Dawn keeps across re-renders, so it
    // is safe to hold and to observe.
    this.productInfo = document.querySelector('product-info');

    if (!this.productInfo || !this.mainButton()) {
      // Nothing to mirror — a bar that cannot add to cart is worse than none.
      this.remove();
      return;
    }

    this.button.addEventListener('click', this.forwardClick);
    this.observeVisibility();
    this.observeState();
    this.sync();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.forwardClick);
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  /* ---- live lookups (never cached) ---- */

  mainButton() {
    return this.productInfo && this.productInfo.querySelector('[name="add"]');
  }

  mainPriceText() {
    if (!this.productInfo) return '';
    // The regular or sale price only — the sticky bar is not the place for a
    // compare-at strike-through (DESIGN.md: curation, not discounting).
    const price = this.productInfo.querySelector(
      '.price__container .price-item--sale, .price__container .price-item--regular'
    );
    return price ? price.textContent.trim() : '';
  }

  variantLabel() {
    const picker = this.productInfo && this.productInfo.querySelector('variant-selects');
    if (!picker) return '';

    const values = [];
    picker.querySelectorAll('input:checked').forEach((input) => values.push(input.value));
    picker.querySelectorAll('select').forEach((select) => {
      if (select.selectedOptions[0]) values.push(select.selectedOptions[0].value);
    });

    return values.join(' · ');
  }

  /* ---- behaviour ---- */

  forwardClick = (event) => {
    event.preventDefault();
    const target = this.mainButton();
    if (target) target.click();
  };

  /* The bar appears only once the real Add to cart has scrolled ABOVE the
     viewport, so the two are never on screen together. An always-visible bar
     was tried and rejected (2026-08-17): it duplicated the in-page button.
     IKEA can keep its bar permanently because its mobile page has no large
     in-page button at all — ours does, so the bar has to yield to it.

     Position is measured on scroll rather than with an IntersectionObserver
     because Dawn replaces the button node on every variant change; an observer
     bound at load would end up watching a detached node and silently stop
     firing. Re-querying per frame cannot go stale. */
  observeVisibility() {
    let ticking = false;

    this.onScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;

        const button = this.mainButton();
        if (!button) return;

        // Once she is past the button the bar stays, all the way to the
        // bottom. The end of the page is the highest-intent moment — she has
        // read everything and decided — so hiding the buy button there is the
        // worst possible time to do it. The footer is kept clear by page
        // padding in the stylesheet, not by taking the bar away.
        const scrolledPast = button.getBoundingClientRect().bottom < 0;

        this.toggleAttribute('data-visible', scrolledPast);
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  /* Watch the whole buy area rather than the price node: Dawn swaps that node
     out on every variant change, and an observer attached to it would be
     watching a node no longer on the page. */
  observeState() {
    if (!('MutationObserver' in window)) return;

    this.mutationObserver = new MutationObserver(() => this.sync());
    this.mutationObserver.observe(this.productInfo, {
      attributes: true,
      attributeFilter: ['disabled', 'checked', 'value'],
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  sync() {
    const main = this.mainButton();
    if (!main) return;

    const label = main.textContent.trim();
    if (label && this.button.textContent.trim() !== label) this.button.textContent = label;

    this.button.disabled = main.disabled;

    const price = this.mainPriceText();
    if (this.priceTarget && price) this.priceTarget.textContent = price;

    const variant = this.variantLabel();
    if (this.variantTarget && variant) this.variantTarget.textContent = variant;
  }
}

if (!customElements.get('joykids-sticky-atc')) {
  customElements.define('joykids-sticky-atc', JoykidsStickyAtc);
}
